import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DemoScheduler = () => {
  const [selectedDemo, setSelectedDemo] = useState('virtual-lab-tour');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    title: '',
    organizationType: '',
    timeSlot: '',
    interests: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const demoTypes = [
    {
      id: 'virtual-lab-tour',
      name: 'Virtual Lab Tour',
      duration: '30 minutes',
      description: 'Interactive walkthrough of our AI/Robotics learning environments',
      features: ['Live instructor demonstration', 'Q&A session', 'Custom configuration preview'],
      icon: 'Monitor'
    },
    {
      id: 'curriculum-consultation',
      name: 'Curriculum Consultation',
      duration: '45 minutes',
      description: 'Detailed discussion about integrating our solutions into your curriculum',
      features: ['Curriculum mapping', 'Implementation timeline', 'Faculty training overview'],
      icon: 'BookOpen'
    },
    {
      id: 'roi-strategy-session',
      name: 'ROI Strategy Session',
      duration: '60 minutes',
      description: 'Comprehensive analysis of potential returns and implementation strategy',
      features: ['Custom ROI analysis', 'Implementation roadmap', 'Budget planning'],
      icon: 'TrendingUp'
    }
  ];

  const organizationTypes = [
    { value: 'university', label: 'University/College' },
    { value: 'community-college', label: 'Community College' },
    { value: 'k12-school', label: 'K-12 School District' },
    { value: 'corporate', label: 'Corporate Enterprise' },
    { value: 'government', label: 'Government Agency' },
    { value: 'nonprofit', label: 'Non-Profit Organization' },
    { value: 'training-provider', label: 'Training Provider' }
  ];

  const timeSlots = [
    { value: 'morning-est', label: '9:00 AM - 12:00 PM EST' },
    { value: 'afternoon-est', label: '1:00 PM - 5:00 PM EST' },
    { value: 'evening-est', label: '6:00 PM - 8:00 PM EST' },
    { value: 'morning-pst', label: '9:00 AM - 12:00 PM PST' },
    { value: 'afternoon-pst', label: '1:00 PM - 5:00 PM PST' },
    { value: 'flexible', label: 'Flexible - Contact me to schedule' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 2000);
  };

  const selectedDemoType = demoTypes.find(demo => demo.id === selectedDemo);

  if (isSubmitted) {
    return (
      <section className="py-20 bg-gradient-to-br from-primary to-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="holographic-card p-12 bg-card/50 backdrop-blur-md text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-success to-accent rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={40} color="white" />
            </div>
            
            <h2 className="text-3xl font-bold text-foreground mb-4">Demo Scheduled Successfully!</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Thank you for your interest in Dream Coders Club. We'll send you a calendar invitation and demo preparation materials within the next hour.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="p-4 bg-background/50 rounded-lg">
                <Icon name="Calendar" size={20} className="text-accent mb-2" />
                <div className="text-sm text-muted-foreground">Demo Type</div>
                <div className="font-semibold text-foreground">{selectedDemoType.name}</div>
              </div>
              <div className="p-4 bg-background/50 rounded-lg">
                <Icon name="Clock" size={20} className="text-accent mb-2" />
                <div className="text-sm text-muted-foreground">Duration</div>
                <div className="font-semibold text-foreground">{selectedDemoType.duration}</div>
              </div>
            </div>
            
            <div className="space-y-4">
              <Button
                variant="default"
                size="lg"
                className="morphing-cta text-white font-semibold"
                iconName="Calendar"
                iconPosition="left"
              >
                Add to Calendar
              </Button>
              
              <div className="text-sm text-muted-foreground">
                Questions? Contact us at{' '}
                <a href="mailto:demos@dreamcodersclub.com" className="text-accent hover:underline">
                  demos@dreamcodersclub.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-primary to-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            Schedule Your
            <span className="gradient-text"> Live Demo</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Experience our AI/Robotics education platform firsthand with a personalized demonstration tailored to your needs
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Demo Types */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-foreground mb-6">Choose Your Demo Experience</h3>
            
            {demoTypes.map((demo) => (
              <div
                key={demo.id}
                className={`holographic-card p-6 cursor-pointer transition-all duration-300 ${
                  selectedDemo === demo.id
                    ? 'bg-accent/10 border-accent shadow-glow'
                    : 'bg-card hover:bg-accent/5 border-border'
                }`}
                onClick={() => setSelectedDemo(demo.id)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    selectedDemo === demo.id
                      ? 'bg-accent text-white' :'bg-muted text-muted-foreground'
                  }`}>
                    <Icon name={demo.icon} size={24} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-lg font-semibold text-foreground">{demo.name}</h4>
                      <span className="text-sm text-accent font-medium">{demo.duration}</span>
                    </div>
                    
                    <p className="text-muted-foreground mb-4">{demo.description}</p>
                    
                    <div className="space-y-2">
                      {demo.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Icon name="Check" size={14} className="text-success" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedDemo === demo.id
                      ? 'border-accent bg-accent' :'border-muted'
                  }`}>
                    {selectedDemo === demo.id && (
                      <Icon name="Check" size={14} color="white" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Column - Booking Form */}
          <div className="holographic-card p-8 bg-card/50 backdrop-blur-md">
            <div className="flex items-center space-x-3 mb-6">
              <Icon name="Calendar" size={24} className="text-accent" />
              <h3 className="text-xl font-bold text-foreground">Book Your Demo</h3>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  type="text"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  required
                />
                
                <Input
                  label="Last Name"
                  type="text"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  required
                />
              </div>

              <Input
                label="Email Address"
                type="email"
                placeholder="john.doe@university.edu"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />

              <Input
                label="Phone Number"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                required
              />

              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Organization"
                  type="text"
                  placeholder="University Name"
                  value={formData.organization}
                  onChange={(e) => handleInputChange('organization', e.target.value)}
                  required
                />
                
                <Input
                  label="Job Title"
                  type="text"
                  placeholder="Dean, CTO, etc."
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
              </div>

              <Select
                label="Organization Type"
                options={organizationTypes}
                value={formData.organizationType}
                onChange={(value) => handleInputChange('organizationType', value)}
                placeholder="Select organization type"
                required
              />

              <Select
                label="Preferred Time Slot"
                options={timeSlots}
                value={formData.timeSlot}
                onChange={(value) => handleInputChange('timeSlot', value)}
                placeholder="Select preferred time"
                required
              />

              <Input
                label="Specific Interests or Questions"
                type="text"
                placeholder="What would you like to focus on during the demo?"
                value={formData.interests}
                onChange={(e) => handleInputChange('interests', e.target.value)}
              />

              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                className="morphing-cta text-white font-semibold"
                loading={isSubmitting}
                iconName="Calendar"
                iconPosition="left"
              >
                {isSubmitting ? 'Scheduling...' : 'Schedule Demo'}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                By scheduling a demo, you agree to our{' '}
                <a href="#" className="text-accent hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-accent hover:underline">Privacy Policy</a>
              </div>
            </form>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 text-center">
          <div className="flex flex-wrap justify-center items-center gap-8 mb-6">
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Icon name="Shield" size={16} className="text-success" />
              <span className="text-sm">SOC 2 Compliant</span>
            </div>
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Icon name="Lock" size={16} className="text-success" />
              <span className="text-sm">FERPA Compliant</span>
            </div>
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Icon name="Award" size={16} className="text-success" />
              <span className="text-sm">ISO 27001 Certified</span>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground">
            Join 500+ institutions already transforming their education programs
          </p>
        </div>
      </div>
    </section>
  );
};

export default DemoScheduler;